import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { fetchCabMeetings } from '../../../services/cabScheduleService';
import { useDateFormatter } from '../../../hooks/useDateFormatter';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

interface CabMeetingsTabProps {
  cabDefinitionId: string;
}

const CabMeetingsTab: React.FC<CabMeetingsTabProps> = ({ cabDefinitionId }) => {
  const { formatDate, formatTime } = useDateFormatter();
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['cab-meetings', cabDefinitionId, page, pageSize, appliedSearch],
    queryFn: () =>
      fetchCabMeetings({
        cabDefinitionId,
        page,
        pageSize,
        search: appliedSearch || undefined,
      }),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const formatDateTime = (iso: string) => {
    if (!iso) return '—';
    return `${formatDate(iso)} ${formatTime(iso)}`;
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchInput.trim());
    setPage(1);
  };

  return (
    <div className="flex flex-col min-h-[320px]">
      <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center gap-2">
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search meetings..."
              className="pl-8 pr-3 py-1.5 w-56 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <button type="submit" className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50">
            Search
          </button>
        </form>
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading CAB meetings...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <p className="text-sm font-medium">No CAB meetings to display.</p>
            <p className="text-xs mt-1">Meetings are generated when schedule entries are saved.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Meeting Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Scheduled Start</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Scheduled End</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((meeting) => (
                <tr key={meeting.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm">
                    <Link
                      to={`/admin/cab-meeting-management/cab-meetings/${meeting.id}`}
                      className="text-indigo-600 font-medium hover:text-indigo-800 hover:underline"
                    >
                      {meeting.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900">{formatDateTime(meeting.scheduledStart)}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{formatDateTime(meeting.scheduledEnd)}</td>
                  <td className="px-4 py-2 text-sm">
                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {meeting.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900">{meeting.active ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > 0 && (
        <div className="px-4 py-2 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span>Show</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1"
            >
              {PAGE_SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <span>
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
            </span>
            {isFetching && !isLoading && <span className="text-xs text-gray-400">Updating...</span>}
          </div>
          <div className="flex gap-1">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1 border rounded disabled:opacity-50">Previous</button>
            <span className="px-2 py-1">Page {page} of {totalPages}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CabMeetingsTab;
